/**
 * One module's mega panel: EVERYTHING inside the module at once, at any
 * depth. The module's own screens are tiles; each sub-folder is a column, and
 * a folder inside it nests as an indented sub-heading with its entries below
 * — recursively, so a screen five folders down is on the panel like any
 * other. The aside carries Pinned and Recently opened.
 *
 * The entries are a WAI-ARIA menu (the modules row's popup): Up/Down walk them
 * in reading order, Home/End jump, Left/Right move to the neighbouring module,
 * Esc closes and returns to the module. Folders are labelled groups, never
 * entries — a folder opens nothing. Right-click / the context-menu key on a
 * screen offers Pin / Unpin. The aside is ordinary Tab-reachable content.
 */
import { Clock, Star } from 'lucide-react'
import { useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import { tw } from '../../../testHooks'
import { NavIcon } from '../../nav/navIcons'
import { PinnedList, RecentList, useEntryMenu } from '../../nav/parts/ShellLists'
import { S } from '../../nav/tokens'
import type { NavTreeNode } from '../../nav/tree'
import type { ShellNavModel } from '../../nav/useShellNav'
import type { PanelFocus } from './ModulesBar'

/** Indent per nesting level; deeper than this keeps the last step, so a very
 *  deep menu stays readable in a 250px column (the sub-headings still name
 *  every level). */
const INDENT = 16
const MAX_INDENT_LEVELS = 5

export interface MegaFocusRequest { seq: number; target: PanelFocus }

export interface MegaPanelProps {
  model: ShellNavModel
  node: NavTreeNode
  panelId: string
  /** Close; `refocus` returns focus to the module button. */
  onClose: (refocus: boolean) => void
  /** Left/Right inside the panel: the neighbouring module. */
  onSide: (dir: -1 | 1) => void
  focusRequest: MegaFocusRequest | null
}

type EntryMenu = ReturnType<typeof useEntryMenu>

interface Ctx {
  model: ShellNavModel
  entryMenu: EntryMenu
  panelId: string
  picked: () => void
  /** The one entry in the tab order (roving tabindex): Tab from the menu goes
   *  on to the aside instead of walking every entry. */
  tabbableId: string | undefined
  setFocused: (id: string) => void
}

/** The first SCREEN entry in reading order (tiles, then the columns). */
function firstEntryId(tiles: NavTreeNode[], columns: NavTreeNode[]): string | undefined {
  if (tiles[0]) return tiles[0].item.id
  const walk = (nodes: NavTreeNode[]): string | undefined => {
    for (const n of nodes) {
      if (!n.isFolder) return n.item.id
      const inner = walk(n.children)
      if (inner) return inner
    }
    return undefined
  }
  return walk(columns)
}

function countScreens(nodes: NavTreeNode[]): number {
  let n = 0
  for (const c of nodes) n += (c.isFolder ? 0 : 1) + countScreens(c.children)
  return n
}

export function MegaPanel({ model, node, panelId, onClose, onSide, focusRequest }: MegaPanelProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const entryMenu = useEntryMenu(model)
  const [focusedId, setFocused] = useState<string | null>(null)

  // The module's own screens become tiles; anything with entries (a folder,
  // an empty folder too, a website) becomes a column. A module that is itself
  // a screen carrying entries (a website at the top level) leads with its own
  // tile, so the site stays one click away.
  const children = node.children
  const tiles = [
    ...(!node.isFolder ? [node] : []),
    ...children.filter((c) => !c.isFolder && c.children.length === 0),
  ]
  const columns = children.filter((c) => c.isFolder || c.children.length > 0)
  const screens = countScreens(children) + (node.isFolder ? 0 : 1)
  const ctx: Ctx = {
    model, entryMenu, panelId, picked: () => onClose(false), setFocused,
    tabbableId: (focusedId && model.tree.byId.has(focusedId) ? focusedId : null) || firstEntryId(tiles, columns),
  }

  const items = () => [...(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || [])]

  // Keyboard opening lands on an entry: the first, the last, or the first one
  // inside a folder (a breadcrumb crumb asks for that).
  useLayoutEffect(() => {
    if (!focusRequest) return
    const t = focusRequest.target
    const els = items()
    let el: HTMLElement | undefined
    if (t === 'first') el = els[0]
    else if (t === 'last') el = els[els.length - 1]
    else {
      const group = [...(menuRef.current?.querySelectorAll<HTMLElement>('[data-tw-mega-folder]') || [])]
        .find((g) => g.dataset.twMegaFolder === t.folderId)
      el = group?.querySelector<HTMLElement>('[role="menuitem"]') || els[0]
    }
    // An empty module has nothing to land on: the panel itself takes focus so
    // Esc still closes it.
    ;(el || rootRef.current)?.focus()
  }, [focusRequest])

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const els = items()
    const i = els.indexOf(document.activeElement as HTMLElement)
    if (i < 0) return
    const go = (j: number) => { e.preventDefault(); els[(j + els.length) % els.length]?.focus() }
    switch (e.key) {
      case 'ArrowDown': go(i + 1); break
      case 'ArrowUp': go(i - 1); break
      case 'Home': go(0); break
      case 'End': go(els.length - 1); break
      case 'ArrowRight': e.preventDefault(); onSide(1); break
      case 'ArrowLeft': e.preventDefault(); onSide(-1); break
      default:
    }
  }

  const headingId = `${panelId}-h`
  return (
    <div
      ref={rootRef}
      id={panelId}
      tabIndex={-1}
      {...tw('action', 'shell-mega-panel', { module: node.item.obj_name || node.item.id })}
      onKeyDown={(e) => {
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(true) }
      }}
      style={{
        position: 'absolute', left: 0, right: 0, top: '100%',
        maxHeight: 'calc(100vh - 128px)', overflow: 'auto', outline: 'none',
        display: 'flex', flexWrap: 'wrap', alignItems: 'stretch',
        background: S.panel, color: S.text, borderBottom: `1px solid ${S.border}`,
        boxShadow: '0 18px 40px rgba(0, 0, 0, 0.22)',
      }}
    >
      <div style={{ flex: '999 1 560px', minWidth: 0, padding: '22px 28px 26px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, minWidth: 0 }}>
          <span aria-hidden style={{
            width: 34, height: 34, borderRadius: S.radius, flexShrink: 0, background: S.accent, color: S.panel,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <NavIcon node={node.item} size={19} />
          </span>
          <h2 id={headingId} className="tw-sh-clip" style={{ margin: 0, fontSize: 20, fontWeight: 600, minWidth: 0 }}>
            {node.item.title}
          </h2>
          <span style={{ fontSize: 14, color: S.text2, flexShrink: 0 }}>
            {screens} {screens === 1 ? 'screen' : 'screens'}
          </span>
        </div>

        <div ref={menuRef} role="menu" aria-labelledby={headingId} onKeyDown={onMenuKey}>
          {tiles.length > 0 && (
            <div role="group" aria-label={`${node.item.title} screens`} style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '8px 16px',
              marginBottom: columns.length ? 20 : 0,
            }}>
              {tiles.map((t) => <Tile key={t.item.id} node={t} ctx={ctx} />)}
            </div>
          )}
          {columns.length > 0 && (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '18px 24px',
              ...(tiles.length ? { paddingTop: 16, borderTop: `1px solid ${S.border}` } : {}),
            }}>
              {columns.map((c) => <Column key={c.item.id} node={c} ctx={ctx} />)}
            </div>
          )}
          {tiles.length === 0 && columns.length === 0 && (
            <p role="none" style={{ margin: 0, fontSize: 14, color: S.text2 }}>No screens in this folder yet.</p>
          )}
        </div>
      </div>

      <aside className="tw-mega-aside" aria-label="Pinned and recently opened" style={{
        flex: '1 1 300px', maxWidth: 380, minWidth: 0, padding: '20px 16px 22px',
        background: S.surface2, borderLeft: `1px solid ${S.border}`,
      }}>
        <AsideHeading icon={<Star size={14} aria-hidden fill={S.star} color={S.starStroke} />}>Pinned</AsideHeading>
        <PinnedList model={model} onPicked={ctx.picked} style={{ margin: '4px 0 0' }} />
        <AsideHeading icon={<Clock size={14} aria-hidden />} style={{ marginTop: 22 }}>Recently opened</AsideHeading>
        <RecentList model={model} onPicked={ctx.picked} />
      </aside>

      {/* Inside the panel, so the panel's outside-click and focus-out rules
          treat the Pin / Unpin menu as part of it. */}
      {entryMenu.ui}
    </div>
  )
}

function AsideHeading({ icon, children, style }: { icon: ReactNode; children: ReactNode; style?: CSSProperties }) {
  return (
    <h3 style={{
      display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 6px', padding: '0 6px',
      fontSize: 13, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: S.text2,
      ...style,
    }}>
      {icon}
      {children}
    </h3>
  )
}

/** Props every screen entry shares: menu role, hooks, open, Pin / Unpin. */
function entryProps(node: NavTreeNode, ctx: Ctx, className: string) {
  const { item } = node
  const current = ctx.model.current?.id === item.id
  return {
    type: 'button' as const,
    role: 'menuitem',
    className,
    'data-node-id': item.id,
    tabIndex: ctx.tabbableId === item.id ? 0 : -1,
    onFocus: () => ctx.setFocused(item.id),
    'aria-current': current ? ('page' as const) : undefined,
    ...tw('nav', item.obj_name || item.title, { type: item.obj_type, selected: current }),
    onClick: () => { ctx.model.open(item); ctx.picked() },
    onContextMenu: ctx.entryMenu.onContextMenu(item),
    onKeyDown: ctx.entryMenu.onKeyDown(item),
  }
}

function PinMark({ node, model }: { node: NavTreeNode; model: ShellNavModel }) {
  if (!model.isPinned(node.item)) return null
  return <Star size={13} role="img" aria-label="Pinned" fill={S.star} color={S.starStroke} style={{ flexShrink: 0 }} />
}

function Tile({ node, ctx }: { node: NavTreeNode; ctx: Ctx }) {
  const current = ctx.model.current?.id === node.item.id
  return (
    <button {...entryProps(node, ctx, 'tw-mega-tile')} style={{
      display: 'flex', alignItems: 'center', gap: 12, minHeight: 56, minWidth: 0, padding: '8px 10px',
      border: '1px solid', borderRadius: S.radius, cursor: 'pointer', fontFamily: 'inherit',
      color: S.text, textAlign: 'left',
    }}>
      <span aria-hidden style={{
        width: 36, height: 36, borderRadius: S.radius, flexShrink: 0, background: S.accentWeak, color: S.accent,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <NavIcon node={node.item} size={19} />
      </span>
      <span className="tw-sh-clip" style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: current ? 700 : 600 }}>
        {node.item.title}
      </span>
      <PinMark node={node} model={ctx.model} />
    </button>
  )
}

/** A first-level folder (or a website) inside the module: a column. */
function Column({ node, ctx }: { node: NavTreeNode; ctx: Ctx }) {
  const headId = `${ctx.panelId}-c-${node.item.id}`
  return (
    <div role="group" aria-labelledby={headId} data-tw-mega-folder={node.item.id} style={{ minWidth: 0 }}>
      {/* Only a folder's heading carries the nav hook: a website's own entry
          (the row below) is the one a test clicks. */}
      <div id={headId} role="presentation"
        {...(node.isFolder ? tw('nav', node.item.obj_name || node.item.title, { type: node.item.obj_type, folder: true }) : {})}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 8, marginBottom: 4,
          borderBottom: `2px solid ${S.accent}`, fontSize: 13, fontWeight: 600, letterSpacing: '0.04em',
          textTransform: 'uppercase', color: S.text2, minWidth: 0,
        }}>
        <NavIcon node={node.item} size={16} />
        <span className="tw-sh-clip">{node.item.title}</span>
      </div>
      {/* A website is a screen too: its own entry leads its pages. */}
      {!node.isFolder && <Row node={node} depth={0} ctx={ctx} />}
      {node.children.length
        ? <Rows nodes={node.children} depth={node.isFolder ? 0 : 1} ctx={ctx} />
        : <p role="none" style={{ margin: '6px 8px', fontSize: 13.5, color: S.text2 }}>No screens yet</p>}
    </div>
  )
}

function Rows({ nodes, depth, ctx }: { nodes: NavTreeNode[]; depth: number; ctx: Ctx }) {
  return <>{nodes.map((n) => <RowNode key={n.item.id} node={n} depth={depth} ctx={ctx} />)}</>
}

const indentPx = (depth: number) => Math.min(depth, MAX_INDENT_LEVELS) * INDENT

/** Any level below a column: a folder is a labelled sub-heading over its
 *  entries (recursively); a screen is a row, and a screen that carries
 *  entries of its own lists them beneath it. */
function RowNode({ node, depth, ctx }: { node: NavTreeNode; depth: number; ctx: Ctx }) {
  const { item, children } = node
  if (node.isFolder) {
    const headId = `${ctx.panelId}-f-${item.id}`
    return (
      <div role="group" aria-labelledby={headId} data-tw-mega-folder={item.id}>
        <div id={headId} role="presentation"
          {...tw('nav', item.obj_name || item.title, { type: item.obj_type, folder: true })}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, minWidth: 0,
            padding: `10px 8px 4px ${8 + indentPx(depth)}px`,
            fontSize: 13, fontWeight: 600, color: S.text2,
          }}>
          <NavIcon node={item} size={15} />
          <span className="tw-sh-clip">{item.title}</span>
        </div>
        {children.length
          ? <Rows nodes={children} depth={depth + 1} ctx={ctx} />
          : <p role="none" style={{ margin: 0, padding: `2px 8px 6px ${8 + indentPx(depth + 1)}px`, fontSize: 13.5, color: S.text2 }}>
              No screens yet
            </p>}
      </div>
    )
  }
  return (
    <>
      <Row node={node} depth={depth} ctx={ctx} />
      {children.length > 0 && (
        <div role="group" aria-label={item.title} data-tw-mega-folder={item.id}>
          <Rows nodes={children} depth={depth + 1} ctx={ctx} />
        </div>
      )}
    </>
  )
}

function Row({ node, depth, ctx }: { node: NavTreeNode; depth: number; ctx: Ctx }) {
  const current = ctx.model.current?.id === node.item.id
  return (
    <button {...entryProps(node, ctx, 'tw-mega-row')} style={{
      position: 'relative', display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 40,
      padding: `6px 8px 6px ${8 + indentPx(depth)}px`, border: 'none', borderRadius: 6, cursor: 'pointer',
      fontFamily: 'inherit', fontSize: 14.5, fontWeight: current ? 600 : 400, color: S.text, textAlign: 'left',
    }}>
      <NavIcon node={node.item} size={17} color={S.accent} />
      <span className="tw-sh-clip" style={{ flex: 1, minWidth: 0 }}>{node.item.title}</span>
      <PinMark node={node} model={ctx.model} />
    </button>
  )
}
